import { IMinMaxDefault } from 'fitbeat.models';
import {
  BodyScanTypes,
  IBodyScanData, ITargetInfo,
  ITargetMetricValues,
  MassUnit, MetricToImperialConverter, UserTargetTypes,
} from 'fitbeat.models';
import moment from 'moment';
import { BMIMinMax, BodymetricRateOfChangeConfig } from '../../config/BodymetricRateOfChangeConfig';
import { StatRangesConfig } from '../../config/statrangesconfig';
import { BodyProgressModel } from '../../entities/analytics';
import { UserProfile } from '../../entities/user/userprofile';
import { calculatebodyFatMassFromBodyFatPercentage, round, roundWhole } from './StatHelpers';
import { validTargetRange, WorkoutTargetConfig } from './WorkoutTargetConfig';

export const monthsToDaysCoefficient = 30.417;

export const calcBMI = (userHeightCm: number, userWeight: number) => {
  return userWeight / (userHeightCm ** 2);
};

export const calcWeightForBMI = (userHeightCm: number, bmi: number) => {
  return Math.round(bmi * (userHeightCm ** 2));
};

export const ensureInputIsInMetricUnits = (userInfo: Partial<UserProfile>, value: number) => {
  if (userInfo.useImperialUnit) {
    return MetricToImperialConverter.convertWeightToMetric(value);
  }
  return value;
};

export const calcMinMaxDefaulTarget = (
  userInfo: any,
  requestTime: Date,
  timezone: string,
  userTargetType: UserTargetTypes,
  latestBodyScan: IBodyScanData,
): IMinMaxDefault => {
  if (!userInfo.height || !userInfo.weight || !userInfo.age ||
    !userInfo.longTermGoal || !userInfo.skillLevel || !userInfo.gender) {
    throw new Error(`User info is incomplete`);
  }

  userInfo.weight = latestBodyScan.weight;

  const dailyRateOfChange = getDailyRateOfChange(userInfo, userTargetType);

  switch (userTargetType) {
    case UserTargetTypes.Bodyweight: {
      return calculateMinMaxDefaultTargetForBodyWeight(
        userInfo,
        requestTime,
        timezone,
        dailyRateOfChange,
        latestBodyScan,
      );
    }
    case UserTargetTypes.Musclemass: {
      return calculateMinMaxDefaultTargetForMusclemass(
        userInfo,
        requestTime,
        timezone,
        dailyRateOfChange,
        latestBodyScan,
      );
    }
    case UserTargetTypes.Bodyfat: {
      return calculateMinMaxDefaultTargetForBodyFat(
        userInfo,
        requestTime,
        timezone,
        dailyRateOfChange,
        latestBodyScan,
      );
    }
  }
};

export const calculateMinMaxDefaultTargetForBodyWeight = (
  userInfo: any,
  requestTime: Date,
  timezone: string,
  dailyRateOfChange: number,
  latestBodyScan: IBodyScanData,
): IMinMaxDefault => {
  const userTargetType = UserTargetTypes.Bodyweight;
  const userHeightCm = userInfo.height / 100;
  const daysInSixMonths = getNumberOfDaysInSixMonthsFromNow(requestTime, timezone);
  const weightLossinSixMonths = dailyRateOfChange * daysInSixMonths;
  const sixMonthMin = userInfo.weight - weightLossinSixMonths;

  const minBMI = BMIMinMax.male.min;
  let minimumBodyWeight = calcWeightForBMI(userHeightCm, minBMI);

  if (
    latestBodyScan &&
    latestBodyScan.type !== BodyScanTypes.Manual &&
    latestBodyScan.weight &&
    latestBodyScan.calculatedBodyFat
  ) {
    const fatlessWeight = latestBodyScan.weight * (1 - (latestBodyScan.calculatedBodyFat / 100));
    const bodyFatStatRange = StatRangesConfig.getBodyFatStatRangeForUser(
      userInfo.gender, userInfo.age, userInfo.longTermGoal);
    const bodyFatRangeWeights = StatRangesConfig.calcBodyWeightRanges(
      bodyFatStatRange,
      latestBodyScan.weight,
      fatlessWeight,
    );

    if (!bodyFatRangeWeights) {
      throw new Error('No ranges available for body weight');
    }

    minimumBodyWeight = bodyFatRangeWeights.TooLow;
  }

  const limitTarget = sixMonthMin <= minimumBodyWeight;
  const min = Math.max(sixMonthMin, minimumBodyWeight);
  const max = userInfo.weight;
  const minWeight = userInfo.weight < min ? userInfo.weight : min;
  const defaultWeight = getTargetFromLatestBodyScan(latestBodyScan, userTargetType) || max;

  const validRange = isValidRange(min, max, userTargetType);

  return {
    min: round(minWeight, 10),
    max: round(max, 10),
    default: round(defaultWeight, 10),
    limitTarget,
    validRange,
  };
};

export const calculateMinMaxDefaultTargetForMusclemass = (
  userInfo: any,
  requestTime: Date,
  timezone: string,
  dailyRateOfChange: number,
  latestBodyScan: IBodyScanData,
): IMinMaxDefault => {
  const userTargetType = UserTargetTypes.Musclemass;
  const daysInSixMonths = getNumberOfDaysInSixMonthsFromNow(requestTime, timezone);
  const musclemassInSixMonths = dailyRateOfChange * daysInSixMonths;

  if (!latestBodyScan.calculatedMuscleMassMeterKg) {
    throw new Error(`Calculated muscle mass meter kg is not provided on scan.`);
  }

  const muscleMass = latestBodyScan.calculatedMuscleMassMeterKg;
  const sixMonthMax = musclemassInSixMonths + muscleMass;

  const max = sixMonthMax;
  const min = muscleMass > max ? max : muscleMass;

  const defaultMuscleMass = getTargetFromLatestBodyScan(latestBodyScan, userTargetType) || min;

  return {
    min: round(min, 10),
    max: round(max, 10),
    default: round(defaultMuscleMass, 10),
    limitTarget: false,
    validRange: true,
  };
};

export const calculateMinMaxDefaultTargetForBodyFat = (
  userInfo: any,
  requestTime: Date,
  timezone: string,
  dailyRateOfChange: number,
  latestBodyScan: IBodyScanData,
): IMinMaxDefault => {
  if (!latestBodyScan.calculatedMuscleMassMeterKg || !latestBodyScan.calculatedBodyFat || !latestBodyScan.weight) {
    throw new Error(`Incomplete body scan provided.`);
  }

  const userTargetType = UserTargetTypes.Bodyfat;
  const calculatedBodyFat = latestBodyScan.calculatedBodyFat;
  const absoluteBodyFat = latestBodyScan.weight * (calculatedBodyFat / 100);
  const daysInSixMonths = getNumberOfDaysInSixMonthsFromNow(requestTime, timezone);
  const bodyFatLossInSixMonths = dailyRateOfChange * daysInSixMonths;
  const sixMonthMin = ((absoluteBodyFat - bodyFatLossInSixMonths) / latestBodyScan.weight) * 100;

  const bodyFatRange = StatRangesConfig.getBodyFatStatRangeForUser(
    userInfo.gender,
    userInfo.age,
    userInfo.longTermGoal,
  );

  const bodyFatRanges = StatRangesConfig.getBodyFatRanges(bodyFatRange);

  if (!bodyFatRanges) {
    throw new Error(`No body fat ranges for gender: ${userInfo.gender} and ` +
      `long term goal: ${userInfo.longTermGoal}.`);
  }

  const minimumBodyFat = bodyFatRanges.TooLow;
  const min = Math.max(sixMonthMin, minimumBodyFat);
  const max = calculatedBodyFat;
  const minBodyFat = calculatedBodyFat < min ? calculatedBodyFat : min;
  const defaultBodyFat = getTargetFromLatestBodyScan(latestBodyScan, userTargetType) || max;
  const limitTarget = sixMonthMin <= minimumBodyFat;
  const validRange = isValidRange(min, max, userTargetType);

  return {
    min: round(minBodyFat, 10),
    max: round(max, 10),
    default: round(defaultBodyFat, 10),
    limitTarget,
    validRange,
  };
};

const getNumberOfDaysInSixMonthsFromNow = (requestTime: Date, timezone: string) => {
  const now = moment(requestTime).tz(timezone);
  const sixMonthsFromNow = moment(now).add(6, 'months');
  return moment(sixMonthsFromNow).diff(now, 'days');
};

const getDailyRateOfChange = (userInfo: any, userTargetType: UserTargetTypes) => {
  const targetBaselineMinutes = WorkoutTargetConfig[userInfo.longTermGoal];
  const bodyProgressModel = new BodyProgressModel(
    userInfo, targetBaselineMinutes);

  switch (userTargetType) {
    case UserTargetTypes.Bodyweight: {
      return bodyProgressModel.calculateAssumedDailyLossInMass();
    }
    case UserTargetTypes.Musclemass: {
      return bodyProgressModel.calculateAssumedDailyGainInMuscleMass();
    }
    case UserTargetTypes.Bodyfat: {
      return bodyProgressModel.calculateAssumedDailyLossInMass();
    }
  }
};

export const getDateDurationText = (daysNeeded: number | undefined, now: moment.Moment) => {
  if (!daysNeeded || daysNeeded === 0) { return '0 weeks'; }
  const daysFromNow = now.clone().add(daysNeeded, 'days');
  const monthsNeeded = daysFromNow.diff(now, 'months');
  const fromDateForRemainingDays = daysFromNow.subtract(monthsNeeded, 'months');
  const weeksNeeded = fromDateForRemainingDays.diff(now, 'weeks', true);
  let roundedWeeks = roundWhole(weeksNeeded);
  if (daysNeeded <= 6) { roundedWeeks = 1; }
  const monthText = () => {
    return monthsNeeded === 0 ? '' :
      monthsNeeded > 1 ? `${monthsNeeded} months` :
        `${monthsNeeded} month`;
  };
  const weekText = () => {
    return roundedWeeks === 0 ? '' :
      roundedWeeks > 1 ? `${roundedWeeks} weeks` :
        `${roundedWeeks} week`;
  };
  const spaceText = () => {
    return monthsNeeded === 0 || roundedWeeks === 0 ? '' : ' ';
  };
  return `${monthText()}${spaceText()}${weekText()}`;
};

export const calcTargetDateBasedOnModel =
  (target: number, userInfo: Partial<UserProfile>,
   requestTime: Date, timezone: string, userTargetType: UserTargetTypes,
   targetMetricValues: ITargetMetricValues | undefined,
   isCurrentMetric?: boolean): ITargetInfo => {
    if (!userInfo.height || !userInfo.weight || !userInfo.age ||
      !userInfo.longTermGoal || !userInfo.skillLevel || !userInfo.gender) {
      throw new Error(`User info is incomplete`);
    }

    const now = moment(requestTime).tz(timezone);
    const dailyRateOfChange = getDailyRateOfChange(userInfo, userTargetType);
    let daysNeeded;
    let change;

    if (userTargetType === UserTargetTypes.Bodyweight) {
      const weight = userInfo.weight;
      if (weight === target || isCurrentMetric) {
        change = {
          weightChange: 0,
          weight: target,
        };
        daysNeeded = 0;
      } else {
        const bodyweightNeeded = weight - target;
        daysNeeded = bodyweightNeeded / dailyRateOfChange;
        const weightChange = target - weight;
        change = {
          weightChange: round(weightChange, 100),
          weight: target,
        };
      }

    } else if (userTargetType === UserTargetTypes.Musclemass) {
      if (!targetMetricValues || !targetMetricValues.muscleMass) {
        throw new Error(`Scanned muscle mass info is incomplete`);
      }

      if (
        targetMetricValues.muscleMass === target ||
        isCurrentMetric
      ) {
        change = {
          muscleMassChange: 0,
          muscleMass: target,
        };
        daysNeeded = 0;
      } else {
        const muscleMassNeeded = target - targetMetricValues.muscleMass;
        daysNeeded = muscleMassNeeded / dailyRateOfChange;
        const muscleMassChange = target - targetMetricValues.muscleMass;
        change = {
          muscleMassChange: round(muscleMassChange, 100),
          muscleMass: target,
        };
      }
    } else if (userTargetType === UserTargetTypes.Bodyfat) {
      const bodyFatRange = StatRangesConfig.getBodyFatStatRangeForUser(
        userInfo.gender,
        userInfo.age,
        userInfo.longTermGoal,
      );
      const bodyFatRanges = StatRangesConfig.getBodyFatRanges(bodyFatRange);

      if (!bodyFatRanges) {
        throw new Error(`No body fat ranges for gender: ${userInfo.gender} and ` +
          `long term goal: ${userInfo.longTermGoal}.`);
      }

      const minBodyFat = bodyFatRanges.TooLow;

      if (
        !targetMetricValues ||
        !targetMetricValues.muscleMass ||
        !targetMetricValues.bodyFat ||
        !targetMetricValues.bodyWeight
      ) {
        throw new Error(`Scanned info is incomplete`);
      }

      if (targetMetricValues.bodyFat <= minBodyFat || targetMetricValues.bodyFat === target || isCurrentMetric) {
        change = {
          bodyFatChange: 0,
          bodyFat: target,
        };
        daysNeeded = 0;

      } else {
        const isBodyFatLoss = targetMetricValues.bodyFat >= target;
        const bodyFatNeeded = isBodyFatLoss ?
          targetMetricValues.bodyFat - target :
          targetMetricValues.bodyFat + target;
        const absoluteBodyFat = calculatebodyFatMassFromBodyFatPercentage(bodyFatNeeded, targetMetricValues.bodyWeight);
        daysNeeded = absoluteBodyFat / dailyRateOfChange;
        const bodyFatChange = target - targetMetricValues.bodyFat;

        change = {
          bodyFatChange: round(bodyFatChange, 100),
          bodyFat: target,
        };
      }
    }

    const targetDate = now.clone().add(daysNeeded, 'days');
    const targetMonthsNeeded = targetDate.diff(now, 'months', true);

    return {
      date: targetDate.toDate(),
      shortDate: targetDate.format('D MMM'),
      monthsNeeded: round(targetMonthsNeeded, 10),
      duration: getDateDurationText(daysNeeded, now),
      unit: userTargetType === UserTargetTypes.Bodyfat ? '%' : MassUnit.Killogram,
      dailyRateOfChange,
      targetType: userTargetType,
      ...change,
    };
  };

export const calcWeightTargetDate =
  (targetWeight: number, userInfo: Partial<UserProfile>, requestTime: Date, timezone: string) => {
    if (!userInfo.weight || !userInfo.longTermGoal) {
      throw new Error(`User info is incompleate`);
    }

    const isBodyweightLoss = userInfo.weight > targetWeight;
    let bodyweightNeeded = isBodyweightLoss ?
      userInfo.weight - targetWeight :
      userInfo.weight + targetWeight;
    if (userInfo.weight === targetWeight) {
      bodyweightNeeded = 0;
    }
    const rateOfChange = BodymetricRateOfChangeConfig[userInfo.longTermGoal];
    const targetMonthsNeeded = bodyweightNeeded / Math.abs(rateOfChange);
    const daysNeeded = targetMonthsNeeded * monthsToDaysCoefficient;
    const now = moment(requestTime).tz(timezone);
    const targetDate = now.clone().add(moment.duration(daysNeeded, 'days'));
    const weightChange = targetWeight - userInfo.weight;

    return {
      date: targetDate.toDate(),
      shortDate: targetDate.format('D MMM'),
      monthsNeeded: round(targetMonthsNeeded, 10),
      duration: getDateDurationText(daysNeeded, now),
      weightChange: round(weightChange, 100),
      unit: MassUnit.Killogram,
    };
  };

export const isValidRange = (min: number, max: number, userTargetType: UserTargetTypes): boolean => {
  return max - min >= validTargetRange[userTargetType];
};

export const getTargetFromLatestBodyScan = (
  latestBodyScan: IBodyScanData,
  userTargetType: UserTargetTypes,
): number | undefined => {
  if (latestBodyScan && latestBodyScan.target) {
    switch (userTargetType) {
      case UserTargetTypes.Bodyweight:
        return latestBodyScan.target.weight;
      case UserTargetTypes.Musclemass:
        return latestBodyScan.target.muscleMass;
      case UserTargetTypes.Bodyfat:
        return latestBodyScan.target.bodyFat;
    }
  }

  return undefined;
};
